import type { Shape } from "../adapters/types";

type DrawingShapeKind = Exclude<NonNullable<Shape["shapeKind"]>, "decor">;

const TITLE: Record<DrawingShapeKind, string> = {
  rectangle: "Rectangle",
  polygon: "Polygone",
  circle: "Cercle",
  line: "Ligne",
  image: "Image",
  marker: "Marqueur",
};

const STEP_TEXT: Record<DrawingShapeKind, string[]> = {
  rectangle: [
    "Cliquez sur la carte pour placer le premier coin du rectangle.",
    "Déplacez la souris puis cliquez pour valider le coin opposé.",
  ],
  polygon: [
    "Cliquez sur la carte pour placer le premier sommet du polygone.",
    "Cliquez pour ajouter un sommet, ou cliquez sur le premier sommet pour fermer le polygone.",
  ],
  circle: [
    "Cliquez sur la carte pour placer le centre du cercle.",
    "Déplacez la souris puis cliquez pour valider le rayon.",
  ],
  line: [
    "Cliquez sur la carte pour placer le premier point de la ligne.",
    "Cliquez pour ajouter un point, ou cliquez sur le dernier point posé pour terminer la ligne.",
  ],
  image: [
    "Cliquez pour poser l'image, ou glissez pour choisir sa taille.",
    "Relâchez pour poser l'image (ses proportions sont conservées).",
  ],
  marker: ["Cliquez sur la carte pour poser le marqueur."],
};

/**
 * Tutoriel générique affiché pendant un mode interactif de `@tokamap/core` (dessin d'une forme,
 * édition de la vue par défaut de la carte...) — un seul widget partagé par tous ces modes :
 * `showMessage` affiche un badge (mis en avant visuellement), un titre et un texte quelconques ;
 * `showStep` est une spécialisation pour le dessin (rectangle : 2 clics, premier coin puis
 * coin opposé ; image : un clic pour la poser, ou un glisser pour la dimensionner ; cercle : 2 clics, centre puis point du bord — texte distinct pour chacun malgré
 * le geste identique ; polygone : un clic par sommet, puis un clic sur le premier sommet pour
 * fermer ; ligne : un clic par sommet comme le polygone, mais un clic sur le DERNIER sommet posé
 * pour terminer, puisqu'une ligne ne se referme pas), qui
 * délègue à `showMessage` avec le badge `N/2` et le texte adaptés à `shapeKind`/`step`. Piloté par
 * `createTokaMap`, qui choisit quand l'appeler/le masquer selon le mode et ses propres options
 * (ex. `StartDrawingOptions.skipTutorial` pour le dessin).
 */
export class DrawTutorial {
  private element: HTMLDivElement | null = null;
  private badgeElement: HTMLDivElement | null = null;
  private titleElement: HTMLDivElement | null = null;
  private textElement: HTMLDivElement | null = null;

  constructor(private container: HTMLElement) {}

  showMessage(badge: string, title: string, text: string): void {
    if (!this.element) {
      this.element = document.createElement("div");
      this.element.className = "toka-draw-tutorial";

      this.badgeElement = document.createElement("div");
      this.badgeElement.className = "toka-draw-tutorial__step";
      this.element.appendChild(this.badgeElement);

      const body = document.createElement("div");
      body.className = "toka-draw-tutorial__body";
      this.titleElement = document.createElement("div");
      this.titleElement.className = "toka-draw-tutorial__title";
      body.appendChild(this.titleElement);
      this.textElement = document.createElement("div");
      this.textElement.className = "toka-draw-tutorial__text";
      body.appendChild(this.textElement);
      this.element.appendChild(body);

      this.container.appendChild(this.element);
    }
    this.badgeElement!.textContent = badge;
    this.titleElement!.textContent = title;
    this.textElement!.textContent = text;
  }

  showStep(shapeKind: DrawingShapeKind, step: 1 | 2): void {
    const steps = STEP_TEXT[shapeKind];
    this.showMessage(`${step}/${steps.length}`, TITLE[shapeKind], steps[step - 1]!);
  }

  hide(): void {
    this.element?.remove();
    this.element = null;
    this.badgeElement = null;
    this.titleElement = null;
    this.textElement = null;
  }
}
